import React from 'react';
import { Alert, Box, Button, ExpandableSection, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-react';
import CONSTANTS from 'src/utils/constants';
import AllocationGroup from 'src/models/tp-allocation/AllocationGroup';
import { ACTIONS } from 'src/services/tp-allocation/allocation-groups/AllocationGroupsReducer';
import { DasAllocationGroupsContext } from 'src/services/derived-allocation-sheet/DasAllocationGroupsContext';
import DasAllocationFormulasGrid from './DasAllocationFormulasGrid';
import {
    GroupShareStatus, groupShareStatus, groupShareTotal, hasUnsavedFormulas,
} from 'src/services/derived-allocation-sheet/dasAllocationGroupShares';

/**
 * The group's total share from the last run, against the downstream allocation check: 100% passes, 0 to
 * below 100% passes and allocates part of the balance, and anything above 100% or below 0 is rejected.
 * Nothing shows before the sheet has run, or while the group has formulas that are not saved yet.
 */
function GroupShareTotal({ group }: { group: AllocationGroup }) {
    const { services } = React.useContext(DasAllocationGroupsContext);
    const formulas = group.formulas || [];
    const total = groupShareTotal(formulas);
    if (total === undefined || hasUnsavedFormulas(formulas)) {
        return null;
    }
    const shown = `Total from the last run: ${services.formattingService.formatPercentage(total)}`;
    switch (groupShareStatus(total)) {
        case GroupShareStatus.FULL:
            return <StatusIndicator type="success">{shown}</StatusIndicator>;
        case GroupShareStatus.PARTIAL:
            return <StatusIndicator type="info">{shown}. The group allocates part of the balance.</StatusIndicator>;
        default:
            return <StatusIndicator type="error">{shown}. A group must not allocate more than 100% or less than 0.</StatusIndicator>;
    }
}

/**
 * One Step 3 allocation group, adapted from the TPA group container; keep the two in sync. Edit and Delete
 * group are offered on the editable draft only. The group's formulas show in a grid, with the total of their
 * outputs from the last run; a group-level error from the section shows under it.
 */
export default function DasAllocationGroup({ group }: { group: AllocationGroup }) {
    const { groupsDispatch, viewMode, saving } = React.useContext(DasAllocationGroupsContext);
    const editable = viewMode === CONSTANTS.VIEW_MODE.EDITABLE;

    return (
        <ExpandableSection data-class="polarisExpandableSection" variant="container" defaultExpanded
                           headerText={group.description} headerDescription={group.allocationGroupDescription}
                           headerActions={editable && (
                               <SpaceBetween direction="horizontal" size="xs">
                                   <Button data-class="subPrimaryButton" className={`dasGroupEdit${group.allocationGroupId}`} disabled={saving}
                                           onClick={() => groupsDispatch(ACTIONS.SET_GROUP_BEING_EDITED.withPayload(group))}>Edit</Button>
                                   <Button data-class="subPrimaryButton" className={`dasGroupDelete${group.allocationGroupId}`} disabled={saving}
                                           onClick={() => groupsDispatch(ACTIONS.SET_REMOVE_GROUP_MODAL_PAYLOAD.withPayload(group))}>Delete group</Button>
                               </SpaceBetween>
                           )}>
            <div className="dataSourceTableContainer">
                <DasAllocationFormulasGrid group={group} />
            </div>
            <Box padding={{ top: 's' }} data-class="dasGroupShareTotal"><GroupShareTotal group={group} /></Box>
            {group.errorMessage && (
                <Box padding={{ top: 's' }}>
                    <Alert type="error" header="Allocation group error">{group.errorMessage}</Alert>
                </Box>
            )}
        </ExpandableSection>
    );
}
